import { useState, useEffect } from "react";
import { KeyWithSecret, WorkspaceKeyType } from "@optidev/shared-types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { CopyableCell } from "@/components/ui/copyable-cell";
import { AlertCircle, Loader2 } from "lucide-react";
import { auth } from "@/lib/firebase";
import { workspaceKeysApi } from "@/lib/api/workspace-keys";
import toast from "react-hot-toast";

interface GenerateKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  keyType?: WorkspaceKeyType;
}

export default function GenerateKeyModal({
  isOpen,
  onClose,
  onSuccess,
  keyType = WorkspaceKeyType.CONTENT,
}: GenerateKeyModalProps) {
  const [generating, setGenerating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<KeyWithSecret | null>(null);

  const isConnectionKey = keyType === WorkspaceKeyType.CONNECTION;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setGeneratedKey(null);
    }
  }, [isOpen]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const token = await auth.getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const result = await workspaceKeysApi.generate(token, keyType);

      setGeneratedKey(result);
      toast.success("API key generated successfully");
    } catch (error: any) {
      console.error("Failed to generate key:", error);
      toast.error(error.response?.data?.message || "Failed to generate key");
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    if (generatedKey) {
      onSuccess();
    }
    onClose();
  };

  const handleSavedConfirmation = () => {
    onSuccess();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] w-full sm:max-w-[500px] rounded-lg">
        <DialogHeader>
          <DialogTitle>
            Generate New {isConnectionKey ? "Connection" : "Content"} Key
          </DialogTitle>
          <DialogDescription>
            {isConnectionKey
              ? "Create a key for Supabase Edge Functions to access connection credentials"
              : "Create a new workspace-scoped API key for content signing and webhooks"}
          </DialogDescription>
        </DialogHeader>

        {!generatedKey ? (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              {isConnectionKey
                ? "This key will be automatically injected into your Supabase Edge Functions as OPTIDEV_CONNECTION_KEY_ID and OPTIDEV_CONNECTION_SECRET."
                : "This will generate a new content signing key for your workspace."}
            </p>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={generating}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full sm:w-auto"
              >
                {generating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  "Generate Key"
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <Card className="border-blue-500/20 bg-blue-50/50 dark:bg-blue-900/10">
              <CardContent className="pt-6">
                <div className="flex gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                      Your API key has been generated successfully!
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      You can view the secret anytime by clicking "Show" in the
                      API Keys settings.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Label>Key ID</Label>
              <div className="relative">
                <code className="block text-sm font-mono bg-muted px-3 py-2 pr-12 rounded border">
                  {generatedKey.keyId}
                </code>
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <CopyableCell
                    value={generatedKey.keyId}
                    display=""
                    tooltipLabel="Copy key ID"
                    iconSize={16}
                    iconClassName="text-muted-foreground hover:text-foreground"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Secret (v{generatedKey.version})</Label>
              <div className="relative">
                <code className=" text-sm font-mono bg-muted px-3 py-2 pr-12 rounded border break-all min-h-[2.5rem] flex items-center">
                  {generatedKey.secret}
                </code>
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <CopyableCell
                    value={generatedKey.secret}
                    display=""
                    tooltipLabel="Copy secret"
                    iconSize={16}
                    iconClassName="text-muted-foreground hover:text-foreground"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button onClick={handleSavedConfirmation}>Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
